import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Star, ArrowRight, X, Clock, CreditCard, Package, Sparkles } from 'lucide-react';

const RehabPackagesSection = ({ onBookService }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Packages with fixed prices (easy to edit)
  const packages = [
    {
      id: 'paket-5',
      name: 'Paket 5 obravnav',
      shortName: '5 obravnav',
      sessions: 5,
      price: 360, // Fiksna cena - lahko jo spremenite
      originalPrice: 400,
      validity: '3 mesece',
      featured: false,
      image: '/images/fizioterapevtska-obravnava-pacient.jpg',
      description: 'Idealen paket za začetek rehabilitacije ali preventivno obravnavo. Vključuje 5 individualnih fizioterapevtskih obravnav.',
      benefits: [
        'Individualna obravnava',
        'Prilagojene vaje za doma',
        'Fleksibilno naročanje',
        'Veljavnost: 3 mesece'
      ]
    },
    {
      id: 'paket-10',
      name: 'Paket 10 obravnav',
      shortName: '10 obravnav',
      sessions: 10,
      price: 680, // Fiksna cena - lahko jo spremenite
      originalPrice: 800,
      validity: '6 mesecev',
      featured: true,
      image: 'https://static.wixstatic.com/media/526f7b_ded52b49dc8c423a93884ea801f7fd11~mv2.jpg/v1/fill/w_400,h_240,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/_MI07135-2.jpg',
      description: 'Najbolj priljubljen paket za celostno rehabilitacijo. Vključuje 10 individualnih fizioterapevtskih obravnav s spremljanjem napredka.',
      benefits: [
        'Individualna obravnava',
        'Prilagojene vaje za doma',
        'Spremljanje napredka',
        'Prioritetno naročanje',
        'Veljavnost: 6 mesecev'
      ]
    },
    {
      id: 'paket-15',
      name: 'Paket 15 obravnav',
      shortName: '15 obravnav',
      sessions: 15,
      price: 960, // Fiksna cena - lahko jo spremenite
      originalPrice: 1200,
      validity: '12 mesecev',
      featured: false,
      image: 'https://static.wixstatic.com/media/526f7b_026103b5969b415ab61d91656d840edf~mv2.jpg/v1/fill/w_400,h_240,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/terapevtska_masa%C3%85%C2%BEa2.jpg',
      description: 'Obsežen paket za dolgotrajno rehabilitacijo ali zahtevnejše primere. Vključuje 15 individualnih fizioterapevtskih obravnav z brezplačno kontrolo.',
      benefits: [
        'Individualna obravnava',
        'Prilagojene vaje za doma',
        'Spremljanje napredka',
        'Prioritetno naročanje',
        'Brezplačna kontrola',
        'Veljavnost: 12 mesecev'
      ]
    }
  ];

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSelectedPackage(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedPackage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedPackage]);

  const handleSelectPackage = (pkg) => {
    const serviceObj = {
      id: pkg.id,
      name: pkg.name,
      price: pkg.price,
      duration: pkg.sessions * 45
    };
    setSelectedPackage(null);
    onBookService(serviceObj);
  };

  return (
    <>
      <section
        id="paketi"
        className="py-10 md:py-16 bg-white"
        data-testid="rehab-packages-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-8">
            <span className="inline-block text-[#f87171] font-semibold tracking-wider uppercase text-sm mb-4">
              Rehabilitacijski paketi
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a365d] mb-4">
              Izberite paket za vas
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Pri večjem številu obravnav prihranite
            </p>
          </div>

          {/* Packages Grid - Similar to Services */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {packages.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg)}
                className={`group relative overflow-hidden rounded-2xl aspect-[4/3] transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
                  pkg.featured ? 'ring-2 ring-[#f87171] ring-offset-2' : ''
                }`}
                data-testid={`package-card-${pkg.id}`}
              >
                {/* Background Image */}
                <img loading="lazy" decoding="async"
                  src={pkg.image}
                  alt={pkg.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a365d]/90 via-[#1a365d]/40 to-transparent group-hover:from-[#1a365d]/95 transition-all duration-300" />
                
                {/* Featured Badge */}
                {pkg.featured && (
                  <div className="absolute top-3 left-3 bg-[#f87171] text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
                    <Star className="w-3 h-3 fill-white" />
                    Priljubljen
                  </div>
                )}
                
                {/* Content */}
                <div className="absolute inset-0 p-4 flex flex-col justify-end">
                  <div className="flex items-center gap-2 mb-1">
                    <Package className="w-4 h-4 text-white/80" />
                    <span className="text-white/80 text-sm">{pkg.sessions} obravnav</span>
                  </div>
                  <h3 className="text-white font-bold text-lg md:text-xl leading-tight">
                    {pkg.shortName}
                  </h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[#f87171] font-bold text-lg">{pkg.price} €</span>
                    <span className="text-white/50 line-through text-sm">{pkg.originalPrice} €</span>
                  </div>
                </div>

                {/* Hover hint */}
                <div className="absolute top-3 right-3 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="w-4 h-4 text-white" />
                </div>
              </button>
            ))}
          </div>

          {/* Bottom Info */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-5 py-2">
              <Sparkles className="w-4 h-4 text-[#f87171]" />
              <span className="text-slate-600 text-sm">
                Vsi paketi vključujejo individualno prilagojene vaje
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Package Detail Modal */}
      {selectedPackage && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          onClick={() => setSelectedPackage(null)}
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Modal Content */}
          <div className="min-h-full flex items-end md:items-center justify-center p-0 md:p-4">
            <div 
              className="relative w-full md:w-auto md:max-w-2xl bg-white rounded-t-3xl md:rounded-3xl overflow-hidden shadow-2xl animate-slide-up md:animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setSelectedPackage(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>

              {/* Image */}
              <div className="relative h-48 md:h-64">
                <img loading="lazy" decoding="async"
                  src={selectedPackage.image}
                  alt={selectedPackage.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* Price badge */}
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <div className="bg-[#f87171] text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg">
                    {selectedPackage.price} €
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm text-slate-500 px-3 py-2 rounded-full text-sm line-through">
                    {selectedPackage.originalPrice} €
                  </div>
                </div>

                {/* Featured badge */}
                {selectedPackage.featured && (
                  <div className="absolute top-4 left-4 bg-[#f87171] text-white px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 shadow-lg">
                    <Star className="w-4 h-4 fill-white" />
                    Najbolj priljubljen
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6 md:p-8">
                <span className="text-[#f87171] font-semibold text-sm uppercase tracking-wider">
                  Rehabilitacijski paket
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-[#1a365d] mt-1 mb-4">
                  {selectedPackage.name}
                </h3>
                
                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  {selectedPackage.description}
                </p>

                {/* Info pills */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full">
                    <Package className="w-4 h-4 text-[#1a365d]" />
                    <span className="text-[#1a365d] font-medium text-sm">{selectedPackage.sessions} obravnav</span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full">
                    <Clock className="w-4 h-4 text-[#1a365d]" />
                    <span className="text-[#1a365d] font-medium text-sm">Veljavnost: {selectedPackage.validity}</span>
                  </div>
                  <div className="flex items-center gap-2 bg-green-100 px-4 py-2 rounded-full">
                    <span className="text-green-700 font-medium text-sm">
                      Prihranek: {selectedPackage.originalPrice - selectedPackage.price} €
                    </span>
                  </div>
                </div>

                {/* Benefits */}
                <div className="mb-6">
                  <h4 className="font-semibold text-[#1a365d] mb-3">Vključeno v paket:</h4>
                  <ul className="space-y-2">
                    {selectedPackage.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-[#f87171]/10 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-[#f87171]" />
                        </div>
                        <span className="text-slate-600 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={() => handleSelectPackage(selectedPackage)}
                  className="w-full bg-[#f87171] hover:bg-[#ef4444] text-white rounded-xl py-6 font-semibold text-lg shadow-lg shadow-[#f87171]/30"
                  data-testid={`book-package-${selectedPackage.id}`}
                >
                  Izberite paket
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes fade-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default RehabPackagesSection;
